/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: garry彭
 * @LastEditTime: 2024-06-06 15:47:52
 */
import React, { useEffect, useState } from 'react'
import { Button, Divider, Drawer, Modal, Spin } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import excel_icon from '../../../../images/excel_icon.jpeg'
import FileSaver from 'file-saver'
import CONFIG from '../../../../constants/config'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import {
  fetchCustomerList,
  fetchLocationsByAddress,
  importCustomers
} from '../../../../actions/customer'
import Upload from './Upload'
import { generateExcelParams, importExcel } from '../../../../utils/importExcel'
import OwnerConfig from './OwnerConfig'
import Preview from './Preview'
import Amap from '../../../components/amap'
import sensors from 'sa-sdk-javascript'
import { sensorsRole } from '../../../../utils/sensors'

// 客户导入记录类型
interface CustomerRecord {
  id: number
  address: string
  contractName: string
  contractTelPhone: string
  customerName: string
  ownerMobiles: string[]
  lat: string
  lng: string
  status: number
}

interface BatchImportProps {
  visible: boolean
  onClose: () => void
  onFinish: () => void
}

export default ({ visible, onClose, onFinish }: BatchImportProps) => {
  const [fileList, setFileList] = useState<any[]>([])
  const [customers, setCustomers] = useState<CustomerRecord[]>([])
  const [noOwnerCustomers, setNoOwnerCustomers] = useState<CustomerRecord[]>([])
  const [successNum, setSuccessNum] = useState(0)
  const [errorNum, setErrorNum] = useState(0)
  const [isVisibleResults, setIsVisibleResults] = useState(false)
  const [isVisibleOnlyError, setIsVisibleOnlyError] = useState(false)
  const [ownerConfigVisible, setOwnerConfigVisible] = useState(false)
  const [isVisibleMap, setIsVisibleMap] = useState(false)
  const [allCustomerCount, setAllCustomerCount] = useState(0) // 客户数总数 （存量客户数数 + 导入的客户数）
  const [editRecord, setEditRecord] = useState<CustomerRecord | null>(null)
  const [loading, setLoading] = useState(false)

  const myRole = useAppSelector((state) => state.group.myRole)
  const groupId = useAppSelector((state) => state.group.groupId)
  const isVip = useAppSelector((state) => state.group.isVip)
  const customerConfig = useAppSelector((state) => state.group.customerConfig)

  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!visible) {
      setCustomers([])
      setNoOwnerCustomers([])
      setSuccessNum(0)
      setErrorNum(0)
      setIsVisibleResults(false)
      setIsVisibleOnlyError(false)
      setOwnerConfigVisible(false)
      setFileList([])
      setAllCustomerCount(0)
    }
  }, [visible])

  const reportSaClickEvent = (clickItem) => {
    sensors.track('web_workgroup_customer_info_batch_import_click', {
      clickItem,
      role: sensorsRole(myRole),
      importCustomerNum: customers.length,
      succLocateNum: successNum,
      totalCustomerNum: allCustomerCount,
      groupID: groupId,
      isGroupVIP: isVip
    })
  }

  const getLocationsByAddress = (locations) => {
    const locationList = locations.map((location) => ({
      customerName: location.customerName,
      contractName: location.contractName,
      contractTelPhone: location.contractTelPhone,
      address: location.address,
      ownerMobiles: location.ownerMobiles
    }))

    setLoading(true)

    Promise.all([
      dispatch(
        fetchCustomerList({
          groupID: groupId,
          pageNo: 1,
          pageSize: 1
        })
      ),
      dispatch(
        fetchLocationsByAddress({
          customers: locationList
        })
      )
    ]).then(([existCustomersRes, importCustomersRes]) => {
      if (importCustomersRes.status === 0) {
        const customers = importCustomersRes.customers.map(
          (customer, index) => ({
            ...customer,
            id: index + 1
          })
        )

        setCustomers(customers)
        setSuccessNum(importCustomersRes.successTotal)
        setErrorNum(importCustomersRes.errorTotal)
        setIsVisibleResults(true)

        const totalCount =
          existCustomersRes.totalCustomer + importCustomersRes.total

        if (!isVip && totalCount > 50) {
          sensors.track('web_show_page_openapp_workgroupvip', {
            groupID: groupId,
            place: 'customerInfo',
            functionType: 'customerNum'
          })
        }

        setAllCustomerCount(totalCount)
        setLoading(false)
      }
    })
  }

  const handleImport = (noOwnHandleWay = 0) => {
    const customerData = customers.map((customer) => ({
      address: customer.address,
      contractName: customer.contractName,
      contractTelPhone: customer.contractTelPhone,
      customerName: customer.customerName,
      ownerMobiles: customer.ownerMobiles,
      lat: customer.lat,
      lng: customer.lng
    }))

    return dispatch(
      importCustomers({
        noOwnHandleWay,
        groupID: groupId,
        customers: customerData
      })
    )
  }

  // 确认导入按钮
  const handleImportClick = () => {
    reportSaClickEvent('confirmImport')

    handleImport(0).then((data) => {
      if (data.errorTotal > 0) {
        setOwnerConfigVisible(true)
        setNoOwnerCustomers(
          data.customers.map((item, index) => ({
            id: index + 1,
            ...item
          }))
        )
      } else {
        Modal.success({
          title: '导入成功',
          onOk: onFinish
        })
      }

      if (data.status === 2) {
        Modal.error({
          title: data.msg
        })
      }
    })
  }

  //下载定位失败列表
  const handleLocationsErrorClick = () => {
    reportSaClickEvent('downloadFailCustomer')

    importExcel(
      generateExcelParams,
      customers
        .filter((customer) => !customer.status)
        .map((item) => ({
          ...item,
          ownerMobiles: item.ownerMobiles.join('、')
        }))
    )
  }

  //调整坐标
  const adjustPosition = (
    location: { longitude: number; latitude: number },
    address: string
  ) => {
    if (!editRecord) return

    const { longitude, latitude } = location
    const newRecord: CustomerRecord = {
      ...editRecord,
      lng: String(longitude),
      lat: String(latitude),
      address: address,
      status: 1
    }

    //服务端定位成功或者失败，web都可以调整位置，原状态为成功，就不需要更新定位数量
    if (!editRecord.status) {
      setSuccessNum(successNum + 1)
      setErrorNum(errorNum - 1)
    }

    const newCustomers = customers.map((member) => {
      if (member.id === newRecord.id) {
        return newRecord
      }
      return member
    })

    setCustomers(newCustomers)
    setEditRecord(null)
    setIsVisibleMap(false)
  }

  return (
    <>
      <Drawer
        open={visible}
        size={1400}
        title={
          <div className="batch-import-header">
            <div className="title">批量导入{customerConfig.noun}</div>
            <div className="subtitle">
              为确保导入成功，请严格按照模板里的格式填写客户信息
            </div>
          </div>
        }
        getContainer={false}
        onClose={onClose}
      >
        <div className="batch-import-container">
          <div className="operate-wrapper">
            <div className="step1">
              <div className="title">
                1.下载模板，填写{customerConfig.noun}信息
              </div>
              <img src={excel_icon} className="excel-icon" alt="" />
              <div>《{customerConfig.noun}信息模板》</div>
              <Button
                icon={<DownloadOutlined />}
                className="download-template"
                onClick={() => {
                  if (
                    customerConfig.noun === '客户' ||
                    customerConfig.noun === '点位' ||
                    customerConfig.noun === '门店'
                  ) {
                    FileSaver.saveAs(
                      CONFIG.customInfoExcelUrl,
                      '客户地点模版（有问题联系客服）.xlsx'
                    )
                  } else {
                    FileSaver.saveAs(
                      CONFIG.anotherCustomInfoExcelUrl,
                      '批量导入模板.xlsx'
                    )
                  }
                }}
              >
                立即下载
              </Button>
            </div>
            <div className="step2">
              <div className="title">2.上传填写完毕的Excel文件</div>
              <Upload
                getLocationsByAddress={getLocationsByAddress}
                fileList={fileList}
                setFileList={setFileList}
              />
            </div>
          </div>

          {isVisibleResults && (
            <>
              <Divider className="divider" />
              <Preview
                allCustomerCount={allCustomerCount}
                onAdjust={(record) => {
                  setIsVisibleMap(true)
                  setEditRecord(record)
                }}
                customers={customers}
                successNum={successNum}
                errorNum={errorNum}
                isVisibleOnlyError={isVisibleOnlyError}
                setIsVisibleOnlyError={setIsVisibleOnlyError}
                handleImportClick={handleImportClick}
                handleLocationsErrorClick={handleLocationsErrorClick}
              />
            </>
          )}
        </div>
      </Drawer>

      <OwnerConfig
        visible={ownerConfigVisible}
        onClose={() => setOwnerConfigVisible(false)}
        onConfirm={() => {
          setOwnerConfigVisible(false)
          onFinish()
        }}
        customers={noOwnerCustomers}
        handleImport={handleImport}
      />

      <Modal
        open={loading}
        footer={null}
        closable={false}
        getContainer={false}
        width={304}
      >
        <div className="loading-wrapper">
          <Spin size="large" />
          <div className="loading-text">上传成功，正在解析...</div>
        </div>
      </Modal>

      {isVisibleMap && editRecord && (
        <Amap
          handleOk={(
            location: { longitude: number; latitude: number },
            address: string
          ) => {
            adjustPosition(location, address)
          }}
          handleCancel={() => {
            setIsVisibleMap(false)
          }}
          locateInfo={editRecord}
        />
      )}
    </>
  )
}
